import React, { useState, useEffect, useRef } from 'react';
import { Search, UserCheck, Plus, X } from 'lucide-react';
import { searchReferrers } from '../../api/sales';
import { toast } from 'react-toastify';

const ReferrerAutocomplete = ({ 
  onReferrerChange, 
  selectedReferrer,
  commissionType = 'percentage',
  commissionValue = '',
  onCommissionTypeChange,
  onCommissionValueChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNewReferrerForm, setShowNewReferrerForm] = useState(false);
  const dropdownRef = useRef(null);

  // New referrer form state
  const [newReferrer, setNewReferrer] = useState({
    full_name: '',
    phone: '',
    email: '',
    address: '',
    commission_type: 'percentage',
    commission_value: '5.00'
  });

  // Search referrers
  useEffect(() => {
    const searchReferrerList = async () => {
      if (searchTerm.length < 2) {
        setReferrers([]);
        return;
      }

      setLoading(true);
      try {
        const response = await searchReferrers(searchTerm);
        setReferrers(response.referrers || []);
        setShowDropdown(true);
      } catch (error) {
        toast.error('Failed to search referrers');
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchReferrerList, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectReferrer = (referrer) => {
    onReferrerChange({
      referrer_id: referrer.id,
      referrer_code: referrer.referrer_code,
      full_name: referrer.full_name,
      phone: referrer.phone,
      email: referrer.email || '',
      commission_rate: referrer.commission_rate,
      commission_type: referrer.commission_type || 'percentage',
      fixed_commission_amount: referrer.fixed_commission_amount
    });
    setSearchTerm('');
    setShowDropdown(false);
    toast.success(`Selected referrer: ${referrer.full_name}`);
  };

  const handleNewReferrerChange = (field, value) => {
    setNewReferrer(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const createNewReferrer = () => {
    // Validate required fields
    if (!newReferrer.full_name.trim()) {
      toast.error('Referrer name is required');
      return;
    }
    if (!newReferrer.phone.trim()) {
      toast.error('Referrer phone is required');
      return;
    }

    const commissionVal = parseFloat(newReferrer.commission_value);
    if (isNaN(commissionVal) || commissionVal < 0) {
      toast.error('Commission value must be a valid positive number');
      return;
    }
    
    if (newReferrer.commission_type === 'percentage' && commissionVal > 100) {
        toast.error('Percentage commission cannot exceed 100%');
        return;
    }

    // Pass new referrer data to parent (will be created when sale is saved)
    onReferrerChange({
      referrer_id: null, // Will be created
      referrer_code: null,
      full_name: newReferrer.full_name.trim(),
      phone: newReferrer.phone.trim(),
      email: newReferrer.email.trim(),
      address: newReferrer.address.trim(),
      commission_rate: newReferrer.commission_type === 'percentage' ? commissionVal : 0,
      commission_type: newReferrer.commission_type,
      fixed_commission_amount: newReferrer.commission_type === 'fixed' ? commissionVal : 0,
      // Pass the raw value for immediate UI update in SalesForm
      raw_commission_value: commissionVal
    });

    // Reset form
    setNewReferrer({
      full_name: '',
      phone: '',
      email: '',
      address: '',
      commission_type: 'percentage',
      commission_value: '5.00'
    });
    setShowNewReferrerForm(false);
    toast.success('New referrer details added');
  };

  const clearReferrer = () => {
    onReferrerChange(null);
    setSearchTerm('');
    toast.info('Referrer cleared');
  };

  return (
    <div className="space-y-4">
      {/* Selected Referrer Display */}
      {selectedReferrer && (
        <div className="border border-secondary-lightGray rounded-lg p-4 bg-secondary-offWhite">
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-accent-gold" />
              <h4 className="font-semibold text-primary-black">
                {selectedReferrer.full_name}
              </h4>
            </div>
            <button
              onClick={clearReferrer}
              className="text-red-600 hover:text-red-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-secondary-gray mb-4">
            {selectedReferrer.referrer_code && (
              <p>Code: <span className="font-medium">{selectedReferrer.referrer_code}</span></p>
            )}
            <p>Phone: <span className="font-medium">{selectedReferrer.phone}</span></p>
            {selectedReferrer.email && (
              <p>Email: <span className="font-medium">{selectedReferrer.email}</span></p>
            )}
          </div>
          
          {/* Commission Type Selection */}
          <div className="border-t border-secondary-lightGray pt-4 mt-2">
            <label className="block text-sm font-medium text-primary-black mb-2">
              Commission Type
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <select
                value={commissionType}
                onChange={(e) => onCommissionTypeChange?.(e.target.value)}
                className="px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
              >
                <option value="percentage">% of Profit</option>
                <option value="per_unit">₦ per Unit Sold</option>
                <option value="fixed">₦ Fixed per Sale</option>
              </select>
              <div className="relative">
                <input
                  type="number"
                  step={commissionType === 'percentage' ? '0.1' : '1'}
                  min="0"
                  value={commissionValue}
                  onChange={(e) => onCommissionValueChange?.(e.target.value)}
                  placeholder={
                    commissionType === 'percentage' 
                      ? 'e.g. 5 for 5%' 
                      : commissionType === 'per_unit' 
                        ? 'e.g. 500' 
                        : 'e.g. 5000'
                  }
                  className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-secondary-gray">
                  {commissionType === 'percentage' ? '%' : '₦'}
                </span>
              </div>
            </div>
            <p className="text-xs text-secondary-gray mt-2">
              {commissionType === 'percentage' && 'Commission will be calculated as a percentage of gross profit.'}
              {commissionType === 'per_unit' && 'Commission will be a fixed amount multiplied by total units sold.'}
              {commissionType === 'fixed' && 'Commission will be a flat amount per sale regardless of quantity.'}
            </p>
          </div>
        </div>
      )}

      {/* Search Input */}
      {!selectedReferrer && !showNewReferrerForm && (
        <div className="relative" ref={dropdownRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray w-5 h-5" />
            <input
              type="text"
              placeholder="Search referrer by name, phone, or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchTerm && setShowDropdown(true)}
              className="w-full pl-10 pr-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold focus:border-transparent"
            />
          </div>

          {/* Dropdown */}
          {showDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-secondary-lightGray rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-secondary-gray">Searching...</div>
              ) : referrers.length > 0 ? (
                <div>
                  {referrers.map((referrer) => (
                    <button
                      key={referrer.id}
                      onClick={() => selectReferrer(referrer)}
                      className="w-full px-4 py-3 text-left hover:bg-secondary-offWhite transition-colors border-b border-secondary-lightGray last:border-b-0"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-primary-black">{referrer.full_name}</p>
                          <p className="text-sm text-secondary-gray">{referrer.phone}</p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-xs text-secondary-gray">{referrer.referrer_code}</p>
                          <p className="text-xs text-accent-gold">Commission: {referrer.commission_rate}%</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : searchTerm.length >= 2 ? (
                <div className="p-4 text-center text-secondary-gray">No referrers found</div>
              ) : (
                <div className="p-4 text-center text-secondary-gray">Type to search referrers</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* New Referrer Button */}
      {!selectedReferrer && !showNewReferrerForm && (
        <button
          onClick={() => setShowNewReferrerForm(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-secondary-lightGray rounded-lg text-secondary-gray hover:border-accent-gold hover:text-accent-gold transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Add New Referrer</span>
        </button>
      )}

      {/* New Referrer Form */}
      {showNewReferrerForm && (
        <div className="border border-secondary-lightGray rounded-lg p-4 bg-white">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-primary-black">New Referrer Details</h4>
            <button
              onClick={() => setShowNewReferrerForm(false)}
              className="text-secondary-gray hover:text-red-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-primary-black mb-1">
                Full Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={newReferrer.full_name}
                onChange={(e) => handleNewReferrerChange('full_name', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="Enter referrer name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-black mb-1">
                Phone <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                value={newReferrer.phone}
                onChange={(e) => handleNewReferrerChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="080XXXXXXXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-black mb-1">
                Email
              </label>
              <input
                type="email"
                value={newReferrer.email}
                onChange={(e) => handleNewReferrerChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="referrer@example.com"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-primary-black mb-1">
                Address
              </label>
              <input
                type="text"
                value={newReferrer.address}
                onChange={(e) => handleNewReferrerChange('address', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="Street address"
              />
            </div>

            {/* Flexible Commission Settings for New Referrer */}
            <div className="md:col-span-2 border-t border-secondary-lightGray pt-4 mt-2">
              <label className="block text-sm font-medium text-primary-black mb-2">
                Default Commission Settings <span className="text-red-600">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <select
                  value={newReferrer.commission_type}
                  onChange={(e) => handleNewReferrerChange('commission_type', e.target.value)}
                  className="px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                >
                  <option value="percentage">% of Profit</option>
                  <option value="per_unit">₦ per Unit Sold</option>
                  <option value="fixed">₦ Fixed per Sale</option>
                </select>
                <div className="relative">
                  <input
                    type="number"
                    step={newReferrer.commission_type === 'percentage' ? '0.1' : '1'}
                    min="0"
                    value={newReferrer.commission_value}
                    onChange={(e) => handleNewReferrerChange('commission_value', e.target.value)}
                    placeholder="Value"
                    className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-secondary-gray">
                    {newReferrer.commission_type === 'percentage' ? '%' : '₦'}
                  </span>
                </div>
              </div>
              <p className="text-xs text-secondary-gray mt-2">
                This will be the default commission strategy for this referrer. You can change it for each sale.
              </p>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={createNewReferrer}
              className="flex-1 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600 transition-colors"
            >
              Add Referrer
            </button>
            <button
              onClick={() => setShowNewReferrerForm(false)}
              className="px-4 py-2 border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferrerAutocomplete;
